import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import SectionLoading from '@components/SectionLoading';
import SurveyJsPollRuntime from '@shared/Resources/Poll/Client/SurveyJsPollRuntime';
import { IoMdClipboard } from 'react-icons/io';
import { MentorFormTemplate, SurveyResponse, SurveySchema } from '../types';
import {
  EditorBackButton,
  EditorHeader,
  EditorHeading,
  EditorSubtitle,
  EditorTitle,
  EditorView,
  FormBody,
  FormError,
  FormLoading,
  FormMessage,
  FormSelectorCard,
  SelectedFormName,
  StyledFormControl,
  SurveyWrapper,
} from '../styles';
import { ActivityType } from '@shared/TimeTracker/types';
import TimeTracker from '@shared/TimeTracker';
import { getMentorFormTemplates } from '../services';

type MentorFormEditorProps = {
  userId: string;
  isClinicalMentor: boolean;
  mode: 'add' | 'edit' | 'view';
  formActivityId?: string;
  selectedFormName?: string;
  saving: boolean;
  schema?: SurveySchema;
  initialData?: SurveyResponse;
  onComplete?: (surveyResponse: SurveyResponse, formId?: string) => void;
};

const MentorFormEditor = ({
  mode,
  formActivityId,
  selectedFormName,
  isClinicalMentor,
  userId,
  saving,
  schema,
  initialData,
  onComplete,
}: MentorFormEditorProps) => {
  const [forms, setForms] = useState<MentorFormTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFormId, setSelectedFormId] = useState('');

  const selectedForm = useMemo(() => forms.find((form) => form.id === selectedFormId), [forms, selectedFormId]);
  const surveySchema = mode === 'add' ? selectedForm?.surveySchema : schema;

  const isExistingForm = mode === 'edit' || mode === 'view';
  const isEditing = mode === 'edit';
  const isReadOnly = mode === 'view';
  const timeTrackEnabled = userId && isClinicalMentor && (!!formActivityId || !isExistingForm);

  const loadMentorForms = useCallback(async () => {
    if (mode !== 'add') {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const templates = await getMentorFormTemplates();
      setForms(templates);
    } catch (e) {
      setError('Failed to load mentor forms.');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    loadMentorForms();
  }, [loadMentorForms]);

  return (
    <EditorView>
      {timeTrackEnabled && (
        <TimeTracker activityType={ActivityType.MENTOR_FORM} activityId={formActivityId || null} userId={userId} />
      )}
      <EditorHeader className={isExistingForm ? 'editing' : undefined}>
        <EditorBackButton startIcon={<ArrowBackIcon />} onClick={() => window.history.back()} disabled={saving}>
          Go back
        </EditorBackButton>
        <EditorHeading>
          <EditorTitle className={!isExistingForm ? 'adding' : undefined}>
            {isReadOnly ? 'View form' : isEditing ? 'Edit form' : 'Add New Form'}
          </EditorTitle>
          <EditorSubtitle>
            {isReadOnly
              ? 'Review the submitted form responses'
              : isEditing
                ? 'Update the form and submit your changes'
                : 'Select a form type, and complete the form:'}
          </EditorSubtitle>
        </EditorHeading>
        {!isExistingForm && (
          <FormSelectorCard>
            <StyledFormControl fullWidth size="small">
              <InputLabel id="mentor-form-label">Select Form</InputLabel>
              <Select
                labelId="mentor-form-label"
                id="mentor-form"
                value={selectedFormId}
                label="Select Form"
                onChange={(event) => setSelectedFormId(event.target.value)}
                disabled={isEditing || loading || saving || !forms.length}
              >
                {forms.map((form) => (
                  <MenuItem key={form.id} value={form.id}>
                    {form.name}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </FormSelectorCard>
        )}
        {isExistingForm && selectedFormName && (
          <SelectedFormName title={selectedFormName}>
            <IoMdClipboard aria-hidden />
            <span>{selectedFormName}</span>
          </SelectedFormName>
        )}
      </EditorHeader>
      <FormBody>
        {loading ? (
          <FormLoading>
            <SectionLoading />
          </FormLoading>
        ) : (
          <>
            {mode === 'add' && !forms.length && !loading && !error && (
              <FormMessage>No mentor form templates found.</FormMessage>
            )}
            {!isExistingForm && forms.length > 0 && !selectedForm && !error && (
              <FormMessage>No form selected</FormMessage>
            )}
            {surveySchema && (
              <SurveyWrapper>
                <SurveyJsPollRuntime
                  key={selectedForm?.id}
                  schema={surveySchema}
                  data={initialData}
                  readOnly={saving || isReadOnly}
                  onComplete={(response) => !isReadOnly && onComplete?.(response, selectedForm?.id)}
                />
              </SurveyWrapper>
            )}
            {isExistingForm && !surveySchema && (
              <FormError>This form's template could not be loaded.</FormError>
            )}
            {selectedForm && !selectedForm.surveySchema && (
              <FormError>The selected form does not contain a valid survey.</FormError>
            )}
          </>
        )}
        {error && <FormError>{error}</FormError>}
      </FormBody>
    </EditorView>
  );
};

export default MentorFormEditor;
